// Project a cook-session's overrides into a "live" view of the recipe
// so the Cook-screen renderers see swaps and skips everywhere they
// surface ingredients — not just in the top-of-screen overview.
//
// Problem this solves: useCookSession holds the user's cook-time swaps
// (session.overrides[i].pantryItemId, .skipped) as ephemeral state
// layered on top of the persisted recipe (see useCookSession.js:29-34).
// That layer was honored by the pairing pass in CookMode and by
// CookComplete's "what did you use?" screen, but NOT by the step
// renderer at CookMode.jsx:855-890, which read recipe.ingredients /
// step.uses raw. Swap crepes → tortillas on cook-prep, advance to the
// crepe step, and the step still announced "crepes" — the swap was
// invisible at the moment it mattered most.
//
// Design principle carried forward from useCookSession's docstring:
// this helper is a PURE DERIVATION from (recipe, session, pantry).
// Never store the result. The only write path that materializes the
// derivation is the explicit "save changes as new recipe" action in
// CookComplete — which forks a new user_recipes row and leaves the
// original recipe untouched. Everywhere else, the effective recipe
// is re-derived per render so the source of truth stays the tuple
// (persisted recipe, ephemeral session), not a cached blob that
// drifts.

import { findIngredient } from "../data/ingredients";
import { deriveRowHeader, normalizeForMatch, resolvesToSameCanonical } from "./recipePairing";
import { convertWithBridge, formatQty } from "./unitConvert";

// Apply session overrides to a recipe and return the effective view.
// Shape-preserving: effective.ingredients has the same length as
// recipe.ingredients, one-to-one by index, so callers that zip status
// / pairing results by index (CookMode's ingredientStatus at :317,
// pairRecipeIngredients at :336) keep working without changes.
// Swapped ingredients carry `_swappedFrom: { item, ingredientId }` and
// a fresh `pantryItemId`. Skipped ingredients carry `_skipped: true`
// but stay in the array — dropping them would break step.uses rows
// ("Add the — to the pan" is useless to a cook) and pairing indices.
//
// step.uses entries are rewritten the same way: if a use matches a
// swapped slot by ingredientId or normalized name, it inherits the
// replacement's name/id while keeping the step-level amount/state.
// Intermediate products referenced in step.uses ("reserved pasta
// water", "the roux from step 2") have no match in recipe.ingredients
// and pass through untouched.
//
// session.extras (user-added ingredients) are NOT inlined into
// effective.ingredients — they'd change the array length and break
// the by-index invariant. Callers that want them can read
// session.extras directly; CookComplete already does.
export function applyCookSessionToRecipe(recipe, session, pantry = []) {
  if (!recipe) return recipe;
  const overrides = session?.overrides || {};
  const pantryList = Array.isArray(pantry) ? pantry : [];
  const pantryById = new Map(pantryList.map(p => [p.id, p]));

  // Sanitize residual UI markers off the input. Fork-to-new-recipe
  // calls canonicalizeEffectiveRecipe on save, but v1 forks (before
  // that path shipped) persisted _swappedFrom / _skipped into
  // user_recipes — and without this guard, cooking one of those
  // recipes re-renders the old strikethroughs even though the session
  // is empty. Sanitizing on entry means every effectiveRecipe marker
  // downstream originated from the CURRENT session, nothing inherited.
  const stripMarkers = (obj) => {
    if (!obj) return obj;
    const { _swappedFrom, _brandedFrom, _skipped, _extra, ...rest } = obj;
    return rest;
  };
  const origIngredients = (Array.isArray(recipe.ingredients) ? recipe.ingredients : []).map(stripMarkers);

  // Two rewrite maps, keyed by (ingredientId | normalized name):
  //   swappedKeys  — substitute / skip: step.uses entries matched
  //                  here get strikethrough + "↔ was:" annotation.
  //   brandedKeys  — same-canonical name upgrade (pantry row has
  //                  brand info): step.uses entries matched here
  //                  just take the upgraded display name, no
  //                  strikethrough — this is the "I'm using MY
  //                  Kerrygold, not generic butter" vibe.
  const swappedKeys = new Map();
  const brandedKeys = new Map();

  const effectiveIngredients = origIngredients.map((ing, i) => {
    const ov = overrides[i] || {};
    if (ov.skipped) {
      const marked = { ...ing, _skipped: true };
      if (ing.ingredientId) swappedKeys.set(`id:${ing.ingredientId}`, marked);
      const nm = normalizeForMatch(ing.item || "");
      if (nm) swappedKeys.set(`nm:${nm}`, marked);
      return marked;
    }

    // Library swap — cook picked a bundled canonical that isn't in
    // pantry ("use celery instead of carrots even though I don't
    // have celery stocked"). The override carries id + display name
    // + emoji so we can produce a full substituted ingredient slot
    // without a pantry row to anchor to. No pantryItemId stamped —
    // pairing downstream correctly reports this as missing (the
    // cook declared intent but doesn't own the item).
    if (ov.swapCanonicalId) {
      const subCanon = findIngredient(ov.swapCanonicalId);
      const subName = ov.swapCanonicalName || subCanon?.name || ov.swapCanonicalId;
      const swapped = {
        ...ing,
        item:         subName,
        ingredientId: ov.swapCanonicalId,
        // Preserve step-level state/qty — the cook is saying "celery
        // in the same amount and the same prep," not authoring a new
        // recipe. If that's wrong for a given cook, the inline amount
        // chip already lets them nudge units.
        _swappedFrom: { item: ing.item, ingredientId: ing.ingredientId || null },
      };
      if (ing.ingredientId) swappedKeys.set(`id:${ing.ingredientId}`, swapped);
      const nm = normalizeForMatch(ing.item || "");
      if (nm) swappedKeys.set(`nm:${nm}`, swapped);
      return swapped;
    }

    // Resolve the committed pantry row for this ingredient:
    //   1. explicit session override (user tapped a pantry row in the
    //      swap picker)
    //   2. auto-pair: first ingredient-kind row that resolves to the
    //      same canonical (Tier 1). Quieter than pairRecipeIngredients
    //      — we only care whether SOME matching row exists so we can
    //      lift its branded name into the display.
    let committedRow = null;
    if (ov.pantryItemId) {
      committedRow = pantryById.get(ov.pantryItemId) || null;
    } else if (ing.ingredientId) {
      committedRow = findSameCanonicalPantryRow(ing, pantryList);
    }
    if (!committedRow) return ing;

    const rowCanon = committedRow.ingredientId || committedRow.canonicalId || null;
    const sameCanonical = !!rowCanon && !!ing.ingredientId &&
      resolvesToSameCanonical(rowCanon, ing.ingredientId);

    if (sameCanonical) {
      // Brand upgrade path — swap target (or auto-paired row) is the
      // same canonical as the recipe called for. Lift the branded
      // display name, stamp pantryItemId for the pairing Tier-0
      // short-circuit, but NO _swappedFrom — this isn't a
      // substitution.
      const upgraded = brandedDisplayName(committedRow);
      if (!upgraded || !isDistinctive(upgraded, ing.item)) {
        // Names match — no visible upgrade, just bind the pantry row
        // so downstream pairing/deduction uses it.
        return { ...ing, pantryItemId: committedRow.id };
      }
      const branded = {
        ...ing,
        item:          upgraded,
        ingredientId:  ing.ingredientId,
        pantryItemId:  committedRow.id,
        _brandedFrom:  { item: ing.item, ingredientId: ing.ingredientId || null },
      };
      if (ing.ingredientId) brandedKeys.set(`id:${ing.ingredientId}`, branded);
      const nm = normalizeForMatch(ing.item || "");
      if (nm) brandedKeys.set(`nm:${nm}`, branded);
      return branded;
    }

    // Substitute path — only fires when the user explicitly picked a
    // pantry row whose canonical DIFFERS from the recipe's
    // ingredient. Auto-pair never reaches here (we only auto-pair
    // same-canonical rows above), so an unwanted substitute can't
    // sneak in from pantry state alone.
    const canonical = rowCanon ? findIngredient(rowCanon) : null;
    const swapped = {
      ...ing,
      item:          committedRow.name || canonical?.name || ing.item,
      ingredientId:  rowCanon || ing.ingredientId || null,
      pantryItemId:  committedRow.id,
      _swappedFrom:  { item: ing.item, ingredientId: ing.ingredientId || null },
    };
    if (ing.ingredientId) swappedKeys.set(`id:${ing.ingredientId}`, swapped);
    const nm = normalizeForMatch(ing.item || "");
    if (nm) swappedKeys.set(`nm:${nm}`, swapped);
    return swapped;
  });

  const origSteps = Array.isArray(recipe.steps) ? recipe.steps : [];
  const effectiveSteps = origSteps.map(step => {
    if (!Array.isArray(step.uses) || step.uses.length === 0) return step;
    const cleanedUses = step.uses.map(stripMarkers);
    const newUses = cleanedUses.map(use => {
      // Swap map takes priority over brand map: a substitute
      // always strikes through (even if the original also had a
      // branded variant in pantry we could have upgraded).
      let hit = null;
      let kind = null;
      if (use.ingredientId) hit = swappedKeys.get(`id:${use.ingredientId}`);
      if (!hit) {
        const nm = normalizeForMatch(use.item || "");
        if (nm) hit = swappedKeys.get(`nm:${nm}`);
      }
      if (hit) kind = "swap";
      if (!hit && use.ingredientId) hit = brandedKeys.get(`id:${use.ingredientId}`);
      if (!hit) {
        const nm = normalizeForMatch(use.item || "");
        if (nm) hit = brandedKeys.get(`nm:${nm}`);
      }
      if (hit && !kind) kind = "brand";
      if (!hit) return use;
      if (hit._skipped) {
        return {
          ...use,
          _skipped: true,
          _swappedFrom: { item: use.item, ingredientId: use.ingredientId || null },
        };
      }
      if (kind === "brand") {
        return {
          ...use,
          item:         hit.item,
          ingredientId: hit.ingredientId,
          pantryItemId: hit.pantryItemId,
          _brandedFrom: { item: use.item, ingredientId: use.ingredientId || null },
        };
      }
      return {
        ...use,
        item:         hit.item,
        ingredientId: hit.ingredientId,
        pantryItemId: hit.pantryItemId,
        _swappedFrom: { item: use.item, ingredientId: use.ingredientId || null },
      };
    });
    return { ...step, uses: newUses };
  });

  return { ...recipe, ingredients: effectiveIngredients, steps: effectiveSteps };
}

// First pantry row (kind=ingredient, non-zero) whose canonical
// resolves to the same as the recipe ingredient's. Used for
// auto-brandification when there's no explicit session override.
// Intentionally simple — expiry/FIFO ordering is the deduction
// layer's job; for display-name lifting we just need ONE same-
// canonical row to know the user has that ingredient in a
// distinctive form.
function findSameCanonicalPantryRow(ing, pantryList) {
  if (!ing?.ingredientId) return null;
  for (const p of pantryList) {
    if (!p) continue;
    if ((p.kind || "ingredient") !== "ingredient") continue;
    if (Number(p.amount) <= 0) continue;
    const pCanon = p.ingredientId || p.canonicalId;
    if (!pCanon) continue;
    if (resolvesToSameCanonical(pCanon, ing.ingredientId)) return p;
  }
  return null;
}

// Compose the branded display name for a pantry row:
// "[Brand] [Canonical name]" when the row carries pantry_items.brand,
// else the canonical name, else whatever the user typed as row.name.
function brandedDisplayName(row) {
  return deriveRowHeader(row) || row?.name || null;
}

// True when the pantry row name carries info the recipe ingredient
// doesn't (brand, variant, size hint). Normalized comparison dodges
// case / punctuation noise — "Butter" vs "butter" isn't distinctive,
// "Kerrygold Butter" vs "butter" is.
function isDistinctive(rowName, ingName) {
  const rn = normalizeForMatch(rowName || "");
  const ig = normalizeForMatch(ingName || "");
  if (!rn || !ig) return false;
  return rn !== ig;
}

// Count how many recipe ingredients are actively swapped or skipped in
// the session. Drives the "N SWAPS · originals preserved" chip in the
// CookMode overview so the user knows the golden recipe is intact.
// Only counts pantryItemId and skipped — used-amount edits aren't
// swaps, they're refinements.
export function countActiveSwaps(session) {
  const overrides = session?.overrides || {};
  let n = 0;
  for (const k of Object.keys(overrides)) {
    const o = overrides[k];
    if (o?.pantryItemId || o?.skipped) n++;
  }
  return n;
}

// Build the per-step swap summary — swaps that specifically rewrote a
// step.uses entry. Returns { from, to, skipped } per uses row. Used
// to label the "FOR THIS STEP" tile; on its own it's too narrow for
// the prose banner + tokenizer because step.uses is a CURATED subset
// of the recipe (bundled recipes only list what the step needs RIGHT
// NOW, so "milk, pesto, capers" in the prose may be absent from
// uses). Use recipeSwapSummary below for prose-reach.
export function stepSwapSummary(step) {
  const uses = Array.isArray(step?.uses) ? step.uses : [];
  const out = [];
  for (const u of uses) {
    if (!u?._swappedFrom) continue;
    out.push({
      from:    u._swappedFrom.item || "(ingredient)",
      to:      u._skipped ? null : (u.item || "(ingredient)"),
      skipped: !!u._skipped,
    });
  }
  return out;
}

// Build a recipe-wide brand-upgrade summary: every ingredient whose
// display name was lifted to a pantry row's branded name. Drives the
// prose rewriter that plain-replaces "butter" with "Kerrygold Butter"
// throughout step.instruction / title / tip — no strikethrough,
// because a brand upgrade isn't a substitution.
export function recipeBrandUpgrades(effectiveRecipe) {
  const ingredients = Array.isArray(effectiveRecipe?.ingredients) ? effectiveRecipe.ingredients : [];
  const out = [];
  const seen = new Set();
  for (const ing of ingredients) {
    if (!ing?._brandedFrom || ing._skipped) continue;
    const from = ing._brandedFrom.item;
    const to   = ing.item;
    if (!from || !to) continue;
    const key = from.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({ from, to });
  }
  return out;
}

// Word-boundary, longest-first plain replacement for brand upgrades.
// Unlike tokenizeSwappedInstruction this returns a plain string —
// no strike/after markup — because same-canonical upgrades aren't
// swaps, the cook is literally using this exact ingredient. Apply
// this BEFORE the swap tokenizer so swap markup doesn't get eaten
// by a subsequent brand replace.
export function applyBrandUpgradesToProse(text, upgrades) {
  if (typeof text !== "string" || !text) return text;
  if (!Array.isArray(upgrades) || upgrades.length === 0) return text;
  const ordered = [...upgrades]
    .filter(u => u.from && u.from.trim() && u.to && u.to.trim())
    .sort((a, b) => b.from.length - a.from.length);
  if (ordered.length === 0) return text;
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const pattern = new RegExp(
    `\\b(${ordered.map(u => escape(u.from)).join("|")})\\b`,
    "gi",
  );
  return text.replace(pattern, (match) => {
    const up = ordered.find(u => u.from.toLowerCase() === match.toLowerCase());
    return up ? up.to : match;
  });
}

// Build a recipe-wide swap summary by walking every ingredient in the
// effective recipe that carries _swappedFrom. This is the right input
// for the prose tokenizer: a step's instruction ("Add the 2% milk,
// pesto, and capers") may mention ingredients the step.uses tile
// doesn't list, so step-scoped summaries miss them. Tokenizer is a
// no-op on entries that don't appear in the prose, so passing the
// full recipe list is safe — deduping happens naturally.
export function recipeSwapSummary(effectiveRecipe) {
  const ingredients = Array.isArray(effectiveRecipe?.ingredients) ? effectiveRecipe.ingredients : [];
  const out = [];
  const seen = new Set();
  for (const ing of ingredients) {
    if (!ing?._swappedFrom) continue;
    const from = ing._swappedFrom.item || "(ingredient)";
    const key = from.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push({
      from,
      to:      ing._skipped ? null : (ing.item || "(ingredient)"),
      skipped: !!ing._skipped,
    });
  }
  return out;
}

// Filter a recipe-wide swap list down to the ones relevant for a
// specific step. A swap is "relevant" when either (a) the original
// ingredient name appears in step.instruction prose with a word
// boundary, or (b) the step.uses tile already had _swappedFrom
// stamped on it by applyCookSessionToRecipe. Drives the yellow
// per-step banner — we don't want to blast every swap across every
// step when only one actually applies to what the cook is doing
// right now.
export function relevantSwapsForStep(step, allSwaps) {
  if (!Array.isArray(allSwaps) || allSwaps.length === 0) return [];
  const prose = String(step?.instruction || "");
  const usesHits = new Set();
  const uses = Array.isArray(step?.uses) ? step.uses : [];
  for (const u of uses) {
    if (u?._swappedFrom?.item) usesHits.add(u._swappedFrom.item.toLowerCase());
  }
  const out = [];
  for (const s of allSwaps) {
    const name = (s.from || "").toLowerCase();
    if (!name) continue;
    if (usesHits.has(name)) { out.push(s); continue; }
    if (prose) {
      const escaped = s.from.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      if (new RegExp(`\\b${escaped}\\b`, "i").test(prose)) {
        out.push(s);
      }
    }
  }
  return out;
}

// Freeze an effective recipe into a persistence-ready canonical shape.
// applyCookSessionToRecipe stamps `_swappedFrom`, `_skipped`, and
// `_extra` markers so the cook UI can render swap indicators,
// strikethroughs, and "↔ was: <original>" annotations. Those markers
// are UI state, NOT persistent data — if we write them through to
// user_recipes, the next cook of the fork re-renders those old swaps
// as if they were fresh session overrides ("Using tortillas instead
// of crepes for this step" on a recipe where tortillas is already
// the canon). Fork-to-new-recipe drops them, and also drops any
// ingredient the user skipped this cook — the fork represents "this
// is how I'm making it now," so skipped ingredients simply aren't
// part of the new canon.
//
// ALSO rewrites step prose (step.title, step.instruction, step.tip,
// step.doneCue) and recipe title/subtitle using the swap map BEFORE
// the markers are stripped. Without this, forking butter → Hiland
// Heavy Whipping Cream saves an ingredient list that says "Hiland
// HWC" but step prose that still reads "Pour into the buttered dish"
// — the word "butter" stranded in the prose after _swappedFrom is
// gone has no way to be struck through on the next cook because the
// tokenizer can no longer see a mapping.
export function canonicalizeEffectiveRecipe(recipe) {
  if (!recipe) return recipe;
  const strip = (obj) => {
    if (!obj) return obj;
    const { _swappedFrom, _skipped, _extra, ...rest } = obj;
    return rest;
  };

  // Collect the swap map while markers are still present. Skipped
  // ingredients don't generate a mapping — they're dropped, not
  // renamed, so prose mentions are left intact for the cook to read
  // around ("Season with salt and pepper to taste" stays legible
  // when pepper is skipped; turning that into a replacement would
  // paper over the choice the user made).
  const ingredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
  const swaps = [];
  const seen = new Set();
  for (const ing of ingredients) {
    if (!ing?._swappedFrom || ing._skipped) continue;
    const from = ing._swappedFrom.item;
    const to   = ing.item;
    if (!from || !to) continue;
    const key = from.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    swaps.push({ from, to });
  }

  // Longest-first so "heavy cream" rewrites before "cream" — avoids
  // "heavy <new>" double-rewrites.
  const ordered = [...swaps].sort((a, b) => b.from.length - a.from.length);
  const rewriteProse = (text) => {
    if (typeof text !== "string" || !text || ordered.length === 0) return text;
    const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const pattern = new RegExp(
      `\\b(${ordered.map(s => escape(s.from)).join("|")})\\b`,
      "gi",
    );
    return text.replace(pattern, (match) => {
      const swap = ordered.find(s => s.from.toLowerCase() === match.toLowerCase());
      return swap ? swap.to : match;
    });
  };

  const cleanIngredients = ingredients
    .filter(ing => !ing?._skipped)
    .map(strip);
  const steps = Array.isArray(recipe.steps) ? recipe.steps : [];
  const cleanSteps = steps.map(step => {
    const uses = Array.isArray(step.uses) ? step.uses : [];
    const cleanUses = uses.filter(u => !u?._skipped).map(strip);
    return {
      ...step,
      uses:        cleanUses,
      title:       rewriteProse(step.title),
      instruction: rewriteProse(step.instruction),
      tip:         rewriteProse(step.tip),
      doneCue:     rewriteProse(step.doneCue),
    };
  });

  const out = {
    ...recipe,
    title:    rewriteProse(recipe.title),
    subtitle: rewriteProse(recipe.subtitle),
    ingredients: cleanIngredients,
    steps:       cleanSteps,
  };
  // Transient routing field — a serving-count seed stamped by cook
  // entry points (book COOK NOW, scheduled-meal taps) for CookMode to
  // read. Never recipe content; letting it fossilize into a saved fork
  // would silently re-scale every future cook of that recipe.
  delete out._initialServings;
  return out;
}

// Render step.instruction prose with strikethrough+replacement applied
// for each swap, AND plain tan rewrites for each brand upgrade.
// Deterministic regex substitution — no AI call. Word-boundary match,
// case-insensitive, longest-name-first so "flour tortilla" matches
// before "tortilla". Returns an array of React-renderable tokens;
// callers map them into JSX. Plain-text-only by design — doesn't
// attempt grammatical declension (plural / possessive / verb forms).
// The banner above the prose is the backstop for cases where regex
// can't reach.
//
// Grammar note: `\b` dodges "buttered" / "creamed" naturally because
// there's no word boundary between "butter" and "ed". Verb uses like
// "butter the dish" DO match and become "Kerrygold Butter the dish" —
// slightly awkward, but we accept the tradeoff to surface branded
// names in every mention.
//
// The shape returned is a flat array of:
//   { text: "..." }                          — unchanged prose
//   { strike: "crepes", after: "tortillas" } — substitute swap
//   { strike: "crepes", after: null }        — skipped ingredient
//   { brand: "Kerrygold Butter" }            — brand upgrade (same
//                                              canonical, more specific
//                                              display name)
export function tokenizeSwappedInstruction(instruction, swaps, brandUpgrades = [], amountReplacements = [], opts = {}) {
  // Pre-pass: rewrite authored amount strings ("6 oz") into the
  // chip-displayed amount ("12 tbsp") so the prose follows whatever
  // unit the user chose on the FOR-THIS-STEP chip. Plain text→text
  // substitution; the brand/swap tokenizer below operates on the
  // result. Done up front so the substitution doesn't fight with
  // the brand pattern (different lexical layer entirely — amounts
  // never overlap with ingredient names).
  let working = String(instruction || "");

  // Mixture-id humanization pass: generated recipes name combine
  // products with snake_case ids (step.combine = { id:
  // "garlic_herb_crust", label: "Garlic herb crust" }), and the
  // model sometimes writes the ID into prose — "press into the
  // garlic_herb_crust mixture". Replace each slug-shaped id with its
  // human label (lowercased for mid-sentence flow) and absorb a
  // duplicated trailing noun ("dry_rub_mixture mixture" → "dry rub
  // mixture"). Runs FIRST — slugs never overlap the ingredient /
  // brand / amount layers below. Only underscore-bearing ids are
  // touched; single-word ids ("batter") already read as prose.
  if (working && Array.isArray(opts.mixtureLabels) && opts.mixtureLabels.length > 0) {
    for (const m of opts.mixtureLabels) {
      if (!m?.id || !m?.label || !String(m.id).includes("_")) continue;
      const label = m.label.charAt(0).toLowerCase() + m.label.slice(1);
      const lastWord = label.trim().split(/\s+/).pop();
      const escaped = String(m.id).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(`\\b${escaped}\\b(\\s+${lastWord})?`, "gi");
      working = working.replace(re, label);
    }
  }

  // Smart anchored-amount pass: when the prose carries a modifier
  // word between the number and the canonical ("4 wrapped franks",
  // "2 fresh tomatoes", "3 thinly-sliced onions") the byte-identical
  // pass below misses because the recipe's ingredients[].amount
  // string is just "4 franks" / "2 tomatoes" / "3 onions" — no
  // modifiers. This pass finds (NUMBER + 0–4 modifier words +
  // INGREDIENT_NAME) patterns and scales the leading number by the
  // global servings ratio. Runs FIRST so the byte-identical pass
  // operates on the post-scaling text and a perfect match still
  // gets a chance.
  //
  // Guardrails:
  //   - Number must be the leading token; we never rewrite mid-
  //     sentence numbers without an anchor.
  //   - Anchor must equal an ingredient name (singular OR plural
  //     via small-rule pluralization). Random nouns ("4 minutes")
  //     don't match because "minutes" isn't in the ingredient list.
  //   - Modifier band capped at 4 words so we don't span sentences.
  //   - Skipped when scaleRatio is 1 (no servings adjustment).
  if (working && opts.scaleRatio && opts.ingredientNames && opts.scaleRatio !== 1) {
    working = scaleAnchoredAmounts(working, opts.ingredientNames, opts.scaleRatio);
  }

  // Convert-anchored amount pass: prose like "4 tbsp of butter (taken
  // from the 4 sticks total)" carries a partial-usage amount the byte-
  // identical pass below CANNOT find — "4 tbsp" isn't anywhere in the
  // ingredient list because the row's amount is the TOTAL (32 tbsp /
  // 4 sticks). When the cook picks a different display unit for butter,
  // the partial-usage prose number drifts out of sync. This pass anchors
  // on the canonical name (+ aliases) and converts every (number,
  // ladder-unit, anchor) match to the chip's display unit via
  // convertWithBridge — so "4 tbsp of butter" → "½ stick of butter"
  // when the cook switched butter to sticks.
  //
  // Anchored to the canonical name to avoid false-rewrites: "4 oz of
  // gjetost" stays untouched when butter changed unit, because gjetost
  // isn't in butter's anchor set.
  if (working && Array.isArray(opts.unitConversionTargets) && opts.unitConversionTargets.length > 0) {
    working = convertAnchoredAmounts(working, opts.unitConversionTargets);
  }

  if (working && Array.isArray(amountReplacements) && amountReplacements.length > 0) {
    const ordered = amountReplacements
      .filter(r => r?.from && r?.to && String(r.from).trim() && String(r.to).trim())
      .filter(r => String(r.from).trim() !== String(r.to).trim())
      // Drop pure-number `from` keys (no unit/noun): "\b2" would catch
      // the start of an unrelated number in prose ("2" inside
      // "20 minutes" → "40 minutes"). Amounts are required to carry a
      // unit token, so this only filters malformed/legacy rows.
      .filter(r => !/^[\d.\s¼½¾⅓⅔⅛⅜⅝⅞]+$/.test(String(r.from).trim()))
      // Longest-first so "1 1/2 cups" matches before "1 cups" — both in
      // the dedup below and in the alternation (JS regex alternation is
      // leftmost-wins, not longest, so order is load-bearing).
      .sort((a, b) => String(b.from).length - String(a.from).length);
    if (ordered.length > 0) {
      const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      // SINGLE pass. A sequential loop that reassigns `working` lets one
      // pair's OUTPUT be re-matched by a later pair's `from` (butter
      // "2 tbsp"→"4 tbsp", then re-caught by oil's "4 tbsp"→"8 tbsp" —
      // double-scaling). One combined alternation + a callback map means
      // every match resolves against the ORIGINAL text exactly once.
      const toFor = new Map();
      for (const r of ordered) {
        const key = String(r.from).trim().toLowerCase();
        if (!toFor.has(key)) toFor.set(key, String(r.to).trim());
      }
      // Word-boundary at the start protects against catching a digit
      // inside another number ("16" ≠ "6"); no trailing \\b so units
      // like "fl oz" that contain a space aren't mishandled.
      const pattern = new RegExp(
        `\\b(${ordered.map(r => escape(String(r.from).trim())).join("|")})`,
        "gi",
      );
      working = working.replace(pattern, (m) => toFor.get(m.toLowerCase()) ?? m);
    }
  }
  const base = working;
  if (!base) return [{ text: base }];

  const all = [];
  for (const s of (Array.isArray(swaps) ? swaps : [])) {
    if (!s?.from || !s.from.trim()) continue;
    all.push({ from: s.from, to: s.to, skipped: !!s.skipped, kind: "swap" });
  }
  for (const u of (Array.isArray(brandUpgrades) ? brandUpgrades : [])) {
    if (!u?.from || !u.from.trim() || !u?.to || !u.to.trim()) continue;
    // Swap wins if both swap and brand target the same `from` — the
    // user made an explicit substitution decision that shouldn't be
    // downgraded to a plain rename.
    if (all.some(r => r.from.toLowerCase() === u.from.toLowerCase())) continue;
    all.push({ from: u.from, to: u.to, kind: "brand" });
  }
  if (all.length === 0) return [{ text: base }];

  // Longest-first so "heavy cream" matches before "cream".
  const ordered = [...all].sort((a, b) => b.from.length - a.from.length);
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Tighter boundary than \b. JS \b treats hyphen + apostrophe as
  // non-word boundaries, which means "butter" inside
  // "peanut-butter-like consistency" or inside "butter's softness"
  // would match and get falsely rewritten. Negative lookbehind +
  // lookahead on [\w'-] enforces "this token must stand alone as
  // an English word" — adjacent letters, hyphens, apostrophes, and
  // underscores all suppress the match. Real sentence boundaries
  // (space, comma, period, paren) still allow the match through.
  const pattern = new RegExp(
    `(?<![\\w'-])(${ordered.map(r => escape(r.from)).join("|")})(?![\\w'-])`,
    "gi",
  );

  // Compound-word exclusions. A swap on a single-token canonical
  // ("butter", "cream", "oil") shouldn't fire when the matched
  // token is the second half of a recognized compound ingredient
  // — peanut butter is its own thing, not butter; ice cream is
  // not cream; olive oil is not oil. Longest-first sort already
  // handles this when the compound itself is in the swap list,
  // but for prose that just MENTIONS a compound (without it being
  // an ingredient), we need an explicit suppression. Key = swap-
  // token (lowercase); value = set of preceding-word prefixes
  // that suppress the match.
  const COMPOUND_PREFIX_SUPPRESS = {
    butter: new Set(["peanut", "almond", "cashew", "nut", "cocoa", "apple", "shea", "body"]),
    cream:  new Set(["ice", "sour", "whipped", "heavy", "coconut", "shaving"]),
    oil:    new Set(["olive", "vegetable", "canola", "sesame", "coconut", "peanut", "sunflower", "avocado", "essential"]),
    sauce:  new Set(["soy", "fish", "hot", "barbecue", "bbq", "tomato", "tartar", "worcestershire"]),
    milk:   new Set(["coconut", "almond", "oat", "soy", "rice", "hemp", "cashew"]),
    sugar:  new Set(["brown", "powdered", "confectioners", "icing", "muscovado", "turbinado"]),
  };
  function isSuppressedByCompound(matchStart, lcToken) {
    const suppressSet = COMPOUND_PREFIX_SUPPRESS[lcToken];
    if (!suppressSet) return false;
    // Look back up to 20 chars for the preceding word (skip any
    // hyphen / whitespace / punctuation between). Then test if the
    // preceding word is one of the suppressed prefixes.
    const tail = base.slice(Math.max(0, matchStart - 20), matchStart);
    const prevMatch = tail.match(/([a-z]+)[\s\-]+$/i);
    if (!prevMatch) return false;
    return suppressSet.has(prevMatch[1].toLowerCase());
  }

  const out = [];
  let lastIdx = 0;
  let m;
  while ((m = pattern.exec(base)) !== null) {
    const matchStart = m.index;
    const matched    = m[0];
    const lc = matched.toLowerCase();
    if (isSuppressedByCompound(matchStart, lc)) {
      // Compound-context match — skip the replacement, let the
      // text flow as-is. lastIdx stays put so the suppressed range
      // gets captured by the next text chunk.
      continue;
    }
    if (matchStart > lastIdx) {
      out.push({ text: base.slice(lastIdx, matchStart) });
    }
    const rep = ordered.find(r => r.from.toLowerCase() === lc);
    if (rep?.kind === "brand") {
      out.push({ brand: rep.to });
    } else if (rep?.skipped) {
      out.push({ strike: matched, after: null });
    } else {
      out.push({ strike: matched, after: rep?.to || null });
    }
    lastIdx = matchStart + matched.length;
  }
  if (lastIdx < base.length) out.push({ text: base.slice(lastIdx) });
  return out;
}

// ─── Smart anchored-amount scaling ─────────────────────────────────
// Scale numbers in prose that are anchored to recipe ingredient
// names, even when modifier words sit between the number and the
// name. Used by the prose tokenizer to rewrite "4 wrapped franks"
// → "2 wrapped franks" when the cook adjusted servings 4→2 — the
// byte-identical pass alone misses this because ingredients[].amount
// is "4 franks" (no "wrapped").

const UNICODE_FRACTIONS = {
  "¼": 0.25, "½": 0.5, "¾": 0.75,
  "⅓": 1/3,  "⅔": 2/3,
  "⅛": 0.125, "⅜": 0.375, "⅝": 0.625, "⅞": 0.875,
};

const IRREGULAR_PLURALS = {
  // Recipe-common irregulars. Add more as they surface in prose.
  tomato:    ["tomato", "tomatoes"],
  potato:    ["potato", "potatoes"],
  cherry:    ["cherry", "cherries"],
  berry:     ["berry", "berries"],
  strawberry:["strawberry", "strawberries"],
  blueberry: ["blueberry", "blueberries"],
  raspberry: ["raspberry", "raspberries"],
  leaf:      ["leaf", "leaves"],
  loaf:      ["loaf", "loaves"],
  knife:     ["knife", "knives"],
  half:      ["half", "halves"],
};

// Generate singular + plural variants of an ingredient name. Returns
// an array of lower-cased strings the regex can match against.
function pluralVariants(name) {
  const lc = String(name || "").trim().toLowerCase();
  if (!lc) return [];
  const out = new Set([lc]);
  // Irregulars first.
  if (IRREGULAR_PLURALS[lc]) {
    for (const v of IRREGULAR_PLURALS[lc]) out.add(v);
  }
  // s/es/ies rule for regulars.
  if (lc.endsWith("y") && lc.length > 1 && !"aeiou".includes(lc[lc.length - 2])) {
    out.add(lc.slice(0, -1) + "ies");           // cherry → cherries
  } else if (lc.endsWith("s") || lc.endsWith("x") || lc.endsWith("ch") || lc.endsWith("sh")) {
    out.add(lc + "es");                         // box → boxes
  } else if (!lc.endsWith("s")) {
    out.add(lc + "s");                          // frank → franks
  }
  // Reverse — if the name is plural, generate singular.
  if (lc.endsWith("ies")) out.add(lc.slice(0, -3) + "y");
  else if (lc.endsWith("es") && lc.length > 3) out.add(lc.slice(0, -2));
  else if (lc.endsWith("s")) out.add(lc.slice(0, -1));
  return Array.from(out);
}

// Parse a leading-number token captured by the regex. Handles
// integers, decimals, mixed unicode fractions ("1½"), and bare
// unicode fractions ("½").
function parseLeadingNumber(token) {
  if (!token) return null;
  const t = String(token).trim();
  if (!t) return null;
  // Bare unicode fraction.
  if (UNICODE_FRACTIONS[t]) return UNICODE_FRACTIONS[t];
  // Mixed: "1½" or "1 ½".
  const mixed = t.match(/^(\d+(?:\.\d+)?)\s*([¼½¾⅓⅔⅛⅜⅝⅞])$/);
  if (mixed) {
    return Number(mixed[1]) + (UNICODE_FRACTIONS[mixed[2]] || 0);
  }
  // Plain decimal / integer.
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

// Format a scaled number back to a display string. Prefers nice
// fractions when the value lands cleanly on a unicode fraction
// (1.5 → "1½", 0.5 → "½"); otherwise truncates trailing decimals.
function formatScaledNumber(n) {
  if (!Number.isFinite(n) || n <= 0) return String(n);
  const whole = Math.floor(n);
  const frac = n - whole;
  for (const [sym, v] of Object.entries(UNICODE_FRACTIONS)) {
    // 0.02 tolerance (was 0.01): scaled prose numbers land NEAR
    // fractions constantly and "½" reads better than "0.51" —
    // same loosening as recipeScaling.formatAmount.
    if (Math.abs(frac - v) < 0.02) {
      if (whole === 0) return sym;
      return `${whole}${sym}`;
    }
  }
  if (Number.isInteger(n)) return String(n);
  // Trim trailing zeros: 1.50 → 1.5, 1.20 → 1.2.
  return n.toFixed(2).replace(/\.?0+$/, "");
}

// ─── Unit-conversion target builder ───────────────────────────────
// Given a list of effective recipe ingredients (each with the chip-
// displayed `amount` string already resolved), return the per-
// ingredient targets the prose tokenizer needs to rewrite partial-
// usage amounts. Shared between CookMode (cook session) and AIRecipe
// (preview) so the rewrite is identical wherever recipe steps render.
//
// Each input ingredient should have:
//   - amount: the chip-displayed string (post applyPreferredUnit /
//     any transient session override). The unit token is parsed off
//     the tail.
//   - item: the recipe row's display label (used as an anchor —
//     covers brand-decorated forms like "Best Choice Unsalted Butter"
//     anchoring back to "butter" via canonical.searchAliases).
//   - ingredientId: canonical id for the registry lookup.
//
// Returns: [{ ingredient, displayUnit, anchorNames }] where `ingredient`
// is the bundled canonical (overlay-merged), `displayUnit` is a string
// like "stick" or "ml", and `anchorNames` is a Set<string> of name
// forms that count as references to this ingredient in prose.

// Multi-token universal units the parser must keep intact. Without
// this, "4 fl oz" parses as just "fl" and the unit lookup fails.
// Order matters — longest first.
const MULTI_TOKEN_UNITS = [
  "fluid ounces", "fluid ounce", "fl oz", "fl_oz",
  "half gallon", "half-gallon", "half_gallon",
  "tablespoons", "tablespoon", "teaspoons", "teaspoon",
  "milliliters", "milliliter", "kilograms", "kilogram",
  "ounces", "ounce", "pounds", "pound",
  "grams", "gram", "liters", "liter",
  "cups", "pints", "quarts", "gallons",
];

function parseDisplayUnitFromAmount(amountStr) {
  if (!amountStr) return null;
  const trimmed = String(amountStr).trim();
  if (!trimmed) return null;
  // Strip the leading numeric portion: digits, decimals, unicode
  // fractions, mixed forms, simple x-y ranges. Take whatever follows
  // as the candidate unit phrase.
  const tail = trimmed
    .replace(/^[\s]*[\d¼½¾⅓⅔⅛⅜⅝⅞]+(?:\.\d+)?(?:\s*[¼½¾⅓⅔⅛⅜⅝⅞])?(?:\s*[-–]\s*[\d¼½¾⅓⅔⅛⅜⅝⅞]+(?:\.\d+)?)?\s*/i, "")
    .toLowerCase()
    .trim();
  if (!tail) return null;
  // Multi-token first so "fl oz" beats "fl".
  for (const m of MULTI_TOKEN_UNITS) {
    if (tail === m || tail.startsWith(m + " ") || tail.startsWith(m + ",")) return m;
  }
  // Single identifier: first word (alphabetic + underscore + hyphen).
  const single = tail.match(/^([a-z][a-z_-]*)/);
  return single ? single[1] : null;
}

function buildAnchorNamesForIngredient(canonical, ing) {
  const set = new Set();
  const push = (s) => {
    if (typeof s !== "string") return;
    const v = s.trim().toLowerCase();
    if (v.length < 3) return;
    set.add(v);
    // Naive plural fallback. convertAnchoredAmounts assembles the
    // regex from this set verbatim, so seeding both forms here covers
    // brand-decorated `item` strings ("Best Choice Unsalted Butter"
    // → also "Best Choice Unsalted Butters") that pluralVariants's
    // simple suffix rules would handle for short tokens but miss
    // for multi-word ones.
    if (!v.endsWith("s")) set.add(v + "s");
    else if (v.length > 1) set.add(v.slice(0, -1));
  };
  push(canonical?.name);
  push(canonical?.shortName);
  push(ing?.item);
  if (Array.isArray(canonical?.searchAliases)) {
    for (const a of canonical.searchAliases) push(a);
  }
  return set;
}

export function buildUnitConversionTargets(ingredients) {
  return (Array.isArray(ingredients) ? ingredients : [])
    .map(ing => {
      const canonical = ing?.ingredientId ? findIngredient(ing.ingredientId) : null;
      if (!canonical || !Array.isArray(canonical.units) || canonical.units.length === 0) return null;
      const displayUnit = parseDisplayUnitFromAmount(ing?.amount);
      if (!displayUnit) return null;
      const anchorNames = buildAnchorNamesForIngredient(canonical, ing);
      if (anchorNames.size === 0) return null;
      return { ingredient: canonical, displayUnit, anchorNames };
    })
    .filter(Boolean);
}

// ─── Convert-anchored amount conversion ───────────────────────────
// Sibling of scaleAnchoredAmounts. Where that one multiplies a number
// by a servings ratio (no unit change), this one converts a (number,
// unit) pair into a different unit on the SAME canonical's ladder. The
// anchor pattern is identical — the number must lead, the canonical
// name (or alias / row item / plural) must follow within a few modifier
// words — so "cook for 4 minutes" never gets rewritten because
// "minutes" isn't a butter anchor.
//
// Each target is { ingredient, displayUnit, anchorNames }:
//   - ingredient: the canonical (already density-hydrated upstream)
//   - displayUnit: the chip-displayed unit id ("stick", "g", "ml")
//   - anchorNames: Set<string> of lowercased names that count as
//     "this is referring to this ingredient" — canonical name,
//     shortName, searchAliases, the row's `item`, plus plural
//     variants of each.
//
// The replacement formats via formatQty against the canonical's ladder,
// so unit labels match what the chip shows ("½ stick", not "0.5 stick").

// Common alternate forms for universal units. Ladder-local units
// (stick, wedge, clove, block, tub) get their plural via the ladder
// entry's `aliases` array; this map covers the universal mass/volume
// vocabulary that cooks write in long-form.
const UNIT_ALT_FORMS = {
  tbsp:   ["tbsp", "tbsps", "tablespoon",  "tablespoons"],
  tsp:    ["tsp",  "tsps",  "teaspoon",    "teaspoons"],
  cup:    ["cup",  "cups"],
  oz:     ["oz",   "ozs",   "ounce",       "ounces"],
  lb:     ["lb",   "lbs",   "pound",       "pounds"],
  g:      ["g",    "gram",  "grams"],
  kg:     ["kg",   "kilogram",   "kilograms"],
  mg:     ["mg",   "milligram",  "milligrams"],
  ml:     ["ml",   "milliliter", "milliliters"],
  l:      ["l",    "liter",      "liters"],
  fl_oz:  ["fl oz","fl_oz", "fluid ounce", "fluid ounces"],
  pint:   ["pint", "pints"],
  quart:  ["quart","quarts"],
  gallon: ["gallon","gallons"],
};

function unitMatchForms(ladderEntry) {
  const variants = new Set();
  const id = String(ladderEntry?.id || "").toLowerCase();
  if (!id) return variants;
  variants.add(id);
  const alt = UNIT_ALT_FORMS[id];
  if (alt) for (const v of alt) variants.add(v);
  if (Array.isArray(ladderEntry.aliases)) {
    for (const a of ladderEntry.aliases) {
      const v = String(a).toLowerCase().trim();
      if (v) variants.add(v);
    }
  }
  // Naive plural fallback for ladder-local units that didn't declare
  // an aliases entry.
  if (!variants.has(id + "s")) variants.add(id + "s");
  return variants;
}

function convertAnchoredAmounts(prose, targets) {
  if (!prose || !Array.isArray(targets) || targets.length === 0) return prose;
  let working = String(prose);
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

  for (const target of targets) {
    const { ingredient, displayUnit, anchorNames } = target || {};
    if (!ingredient || !displayUnit) continue;
    if (!Array.isArray(ingredient.units) || ingredient.units.length === 0) continue;
    if (!anchorNames || typeof anchorNames.size !== "number" || anchorNames.size === 0) continue;

    // Map every prose-form of every ladder unit back to its canonical
    // ladder id, so the regex captures "tablespoons" and we feed
    // "tbsp" to convertWithBridge.
    const formToLadderId = new Map();
    for (const u of ingredient.units) {
      for (const form of unitMatchForms(u)) {
        if (!formToLadderId.has(form)) formToLadderId.set(form, u.id);
      }
    }
    if (formToLadderId.size === 0) continue;

    const unitPattern = Array.from(formToLadderId.keys())
      .sort((a, b) => b.length - a.length)  // longest first: "tablespoons" before "tablespoon"
      .map(escape)
      .join("|");
    const anchorPattern = Array.from(anchorNames)
      .sort((a, b) => b.length - a.length)
      .map(escape)
      .join("|");

    // NUMBER: integer/decimal with optional unicode fraction, OR a bare unicode fraction.
    const numberPart = `(\\d+(?:\\.\\d+)?(?:\\s*[¼½¾⅓⅔⅛⅜⅝⅞])?|[¼½¾⅓⅔⅛⅜⅝⅞])`;
    // UNIT: one of the laddered match-forms.
    const unitPart = `(${unitPattern})`;
    // MIDDLE: optional "of"/"of the" plus 0–4 modifier words. At least
    // one whitespace before the anchor.
    const middlePart = `(\\s+(?:of\\s+(?:the\\s+)?)?(?:[a-z][a-z\\-]*\\s+){0,4})`;
    // Leading boundary: \b doesn't fire before unicode fractions
    // (½, ¼, etc.) because they aren't \w. Negative lookbehind for
    // digit / fraction / dot matches what \b would, plus those.
    const leadingBoundary = `(?<![\\w¼½¾⅓⅔⅛⅜⅝⅞.])`;
    const re = new RegExp(
      `${leadingBoundary}${numberPart}\\s+${unitPart}\\b${middlePart}(${anchorPattern})\\b`,
      "gi",
    );

    working = working.replace(re, (full, num, unitForm, mid, anchor) => {
      const amount = parseLeadingNumber(num);
      if (amount == null || amount <= 0) return full;
      const ladderId = formToLadderId.get(String(unitForm).toLowerCase());
      if (!ladderId) return full;
      // Skip self-conversion (matched form already on display unit).
      if (String(ladderId).toLowerCase() === String(displayUnit).toLowerCase()) return full;
      const result = convertWithBridge({ amount, unit: ladderId }, displayUnit, ingredient);
      if (!result || !result.ok || !Number.isFinite(result.value) || result.value <= 0) return full;
      const newQty = formatQty({ amount: result.value, unit: displayUnit }, ingredient);
      if (!newQty) return full;
      return `${newQty}${mid}${anchor}`;
    });
  }
  return working;
}

function scaleAnchoredAmounts(prose, ingredientNames, scaleRatio) {
  if (!prose || !Array.isArray(ingredientNames) || ingredientNames.length === 0) return prose;
  if (!Number.isFinite(scaleRatio) || scaleRatio <= 0 || scaleRatio === 1) return prose;
  // Build the anchor list: every variant of every ingredient name.
  const anchors = new Set();
  for (const name of ingredientNames) {
    for (const v of pluralVariants(name)) anchors.add(v);
  }
  if (anchors.size === 0) return prose;
  const escape = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  // Longest-first so "chicken breasts" matches before "chicken".
  const anchorPattern = Array.from(anchors)
    .sort((a, b) => b.length - a.length)
    .map(escape)
    .join("|");
  // NUMBER capture: integer/decimal optionally followed by a unicode
  // fraction OR a bare unicode fraction. Must start at a word
  // boundary.
  const numberPart = `(\\d+(?:\\.\\d+)?(?:\\s*[¼½¾⅓⅔⅛⅜⅝⅞])?|[¼½¾⅓⅔⅛⅜⅝⅞])`;
  // MIDDLE: 0-4 modifier words separated by whitespace or hyphens
  // (covers "thinly-sliced", "extra-virgin", "freshly-cracked"). At
  // least one whitespace char between number and anchor.
  const middlePart = `(\\s+(?:[a-z][a-z\\-]*\\s+){0,4})`;
  const re = new RegExp(`\\b${numberPart}${middlePart}(${anchorPattern})\\b`, "gi");
  return prose.replace(re, (full, num, mid, name) => {
    // This pass is for UNIT-LESS counts only ("4 wrapped franks",
    // "2 fresh tomatoes"). When the word immediately after the number
    // is a measurement unit, the (already-scaled) byte-identical amount
    // pass owns it — scaling here too would either pre-empt the user's
    // unit-preference rewrite or double-scale. When it's a time/temp
    // word, the number isn't a quantity at all ("cook 2 minutes then
    // add onions" must NOT become "4 minutes"). Bail in both cases.
    const firstMidWord = String(mid).trim().split(/[\s-]+/)[0]?.toLowerCase();
    if (firstMidWord && ANCHOR_STOP_WORDS.has(firstMidWord)) return full;
    const parsed = parseLeadingNumber(num);
    if (parsed == null || parsed <= 0) return full;
    const scaled = parsed * scaleRatio;
    return `${formatScaledNumber(scaled)}${mid}${name}`;
  });
}

// Words that, when they directly follow the number, mean the anchored
// count-scaling pass should stand down: measurement units (owned by the
// byte-identical amount pass) and time/temperature words (not a
// quantity of the ingredient at all).
const ANCHOR_STOP_WORDS = new Set([
  // volume / weight / count units
  "tsp", "teaspoon", "teaspoons", "tbsp", "tablespoon", "tablespoons",
  "cup", "cups", "oz", "ounce", "ounces", "lb", "lbs", "pound", "pounds",
  "g", "gram", "grams", "kg", "ml", "milliliter", "milliliters",
  "l", "liter", "liters", "litre", "litres",
  "pinch", "pinches", "dash", "dashes", "clove", "cloves", "stick", "sticks",
  "can", "cans", "fl", "quart", "quarts", "pint", "pints", "gallon", "gallons",
  // time / temperature — never an ingredient quantity
  "min", "mins", "minute", "minutes", "sec", "secs", "second", "seconds",
  "hour", "hours", "hr", "hrs", "day", "days", "week", "weeks",
  "degree", "degrees",
]);
